import React, { useCallback, useEffect, useState } from 'react';
import { horizonAccessService } from '../../services/api';
import { HorizonAccessEntry, HorizonAccessInstance } from '../../types';
import { formatDateTime } from '../../utils/datetime';
import ScopeFilter, { ScopeSelection } from '../ScopeFilter';

// Two readings of the same log.
//
// "Portals" is the operational one: a row per instance, with what each is
// calling and what is being refused. A portal with no callback secret of its
// own shows as unidentified — its requests arrive, but nothing ties them to it.
//
// "Requests" is the audit one: who read what, under which role, and how wide
// the platform let the answer be. The callers are people looking at other
// people's conversations, so the refusals matter as much as the successes —
// hence the filter that shows nothing else.

type View = 'portals' | 'log';

const nf = new Intl.NumberFormat();

const HorizonAccessTable: React.FC = () => {
  const [view, setView] = useState<View>('portals');
  const [scope, setScope] = useState<ScopeSelection>({});
  const [entries, setEntries] = useState<HorizonAccessEntry[]>([]);
  const [portals, setPortals] = useState<HorizonAccessInstance[]>([]);
  const [detailsVisible, setDetailsVisible] = useState(true);
  const [deniedOnly, setDeniedOnly] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { territory: scope.territory, domain: scope.domain };
      if (view === 'portals') {
        setPortals(await horizonAccessService.instances(params));
      } else {
        const res = await horizonAccessService.list({
          ...params, page, limit: 50, denied_only: deniedOnly || undefined,
        });
        setEntries(res.entries);
        setDetailsVisible(res.details_visible);
        setTotal(res.pagination?.total_count ?? res.entries.length);
      }
    } catch (e) {
      setError('Could not load the Horizon access log');
    } finally {
      setLoading(false);
    }
  }, [view, page, deniedOnly, scope.territory, scope.domain]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="settings-panel">
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap' }}>
        <h2 style={{ marginRight: 'auto' }}>Horizon Access</h2>
        <nav className="settings-subnav">
          <button
            className={`nav-button ${view === 'portals' ? 'active' : ''}`}
            onClick={() => setView('portals')}
          >
            Portals
          </button>
          <button
            className={`nav-button ${view === 'log' ? 'active' : ''}`}
            onClick={() => { setView('log'); setPage(1); }}
          >
            Requests
          </button>
        </nav>
        <ScopeFilter value={scope} onChange={s => { setScope(s); setPage(1); }} />
      </div>

      {error && <div className="error-message">{error}</div>}
      {loading && <div className="small">Loading…</div>}

      {view === 'portals' ? (
        <>
          <p className="small">
            Every Horizon portal that has called this platform, by the key it presented.
            A portal with no callback secret of its own cannot be identified, so its
            requests are listed as unidentified.
          </p>
          <div className="table-container">
          <table className="wide-table">
            <thead>
              <tr>
                <th>Portal</th>
                <th>Horizon host</th>
                <th style={{ textAlign: 'right' }}>Requests</th>
                <th style={{ textAlign: 'right' }}>Refused</th>
                <th style={{ textAlign: 'right' }}>People</th>
                <th style={{ textAlign: 'right' }}>ms</th>
                <th>Source</th>
                <th>Last seen</th>
              </tr>
            </thead>
            <tbody>
              {portals.map(i => (
                <tr key={`${i.instance_id}-${i.instance_label}`}>
                  <td>{i.instance_label || (
                    <span style={{ color: '#b9770e' }}>unidentified</span>
                  )}</td>
                  <td className="monospace small">
                    {i.hostname || '—'}
                    {i.instance_status === 'disabled' && (
                      <div className="small" style={{ color: '#c0392b', fontWeight: 600 }}>disabled</div>
                    )}
                    {i.instance_id !== null && !i.has_secret && (
                      <div className="small" style={{ color: '#b9770e' }}>no callback secret</div>
                    )}
                  </td>
                  <td style={{ textAlign: 'right' }}>{nf.format(i.calls)}</td>
                  <td style={{ textAlign: 'right' }}>
                    {i.denied_calls > 0
                      ? <span style={{ color: '#c0392b', fontWeight: 600 }}>{nf.format(i.denied_calls)}</span>
                      : <span style={{ opacity: 0.5 }}>0</span>}
                  </td>
                  <td style={{ textAlign: 'right' }}>{nf.format(i.distinct_users)}</td>
                  <td style={{ textAlign: 'right' }}>{i.avg_ms ?? '—'}</td>
                  <td className="monospace small">{i.source_ips || '—'}</td>
                  <td className="small">{formatDateTime(i.last_at)}</td>
                </tr>
              ))}
              {!loading && portals.length === 0 && (
                <tr><td colSpan={8}>No Horizon requests recorded yet.</td></tr>
              )}
            </tbody>
          </table>
          </div>
        </>
      ) : (
        <>
          <p className="small">
            {nf.format(total)} recorded request{total === 1 ? '' : 's'}.
            {detailsVisible
              ? ' Search terms and record ids are shown in full.'
              : ' Search terms and record ids are shown to Super Users only — they can contain the names and numbers of recorded parties.'}
            {' '}Entries are kept for 180 days.
          </p>
          <label className="small" style={{ display: 'block', marginBottom: '8px' }}>
            <input
              type="checkbox"
              checked={deniedOnly}
              onChange={e => { setDeniedOnly(e.target.checked); setPage(1); }}
            />{' '}
            Refusals only
          </label>
          <div className="table-container">
          <table className="wide-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Portal</th>
                <th>Who</th>
                <th>Role</th>
                <th>Asked for</th>
                <th>Saw</th>
                <th style={{ textAlign: 'right' }}>Rows</th>
                <th style={{ textAlign: 'right' }}>ms</th>
                <th>Status</th>
                <th>From</th>
              </tr>
            </thead>
            <tbody>
              {entries.map(e => (
                <tr key={e.id}>
                  <td className="small">{formatDateTime(e.created_at)}</td>
                  <td className="small">{e.instance_label || <span style={{ opacity: 0.5 }}>—</span>}</td>
                  <td className="monospace small">{e.party_uid || <span style={{ opacity: 0.5 }}>—</span>}</td>
                  <td className="small">{e.scope || '—'}</td>
                  <td className="small">
                    <span className="monospace">{e.endpoint.replace(/^horizon_|\.php$/g, '')}</span>
                    {(e.search_text || e.subject) && (
                      <div className="monospace small" style={{ opacity: 0.7 }}>
                        {e.search_text ? `“${e.search_text}”` : e.subject}
                      </div>
                    )}
                  </td>
                  {/* The predicate actually applied, after any clamp — how wide
                      the platform let the answer be, not what the role nominally
                      grants. */}
                  <td className="small">{e.visibility || '—'}</td>
                  <td style={{ textAlign: 'right' }}>
                    {e.result_count === null ? '—' : nf.format(e.result_count)}
                  </td>
                  <td style={{ textAlign: 'right' }}>{e.duration_ms ?? '—'}</td>
                  <td className="small">
                    {e.status === 'ok'
                      ? <span style={{ opacity: 0.6 }}>ok</span>
                      : <span style={{ color: '#c0392b' }}>
                          {e.http_status ? `${e.http_status} ` : ''}{e.error_code || e.status}
                        </span>}
                  </td>
                  <td className="monospace small">{e.remote_ip || '—'}</td>
                </tr>
              ))}
              {!loading && entries.length === 0 && (
                <tr><td colSpan={10}>
                  {deniedOnly ? 'No refusals recorded.' : 'No Horizon requests recorded yet.'}
                </td></tr>
              )}
            </tbody>
          </table>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '10px' }}>
            <button disabled={page <= 1 || loading} onClick={() => setPage(p => p - 1)}>Previous</button>
            <span className="small">Page {page}</span>
            <button disabled={page * 50 >= total || loading} onClick={() => setPage(p => p + 1)}>Next</button>
          </div>
        </>
      )}
    </div>
  );
};

export default HorizonAccessTable;
